import React, { useState, useRef } from "react";
import ServiceNowQuestion from "../ServiceNow/ServiceNowQuestion";
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import apis from '../APILibrary/results.json';
import { contactUsRequest } from "../../api/api";
import Spinner from "../../helpers/spinner";

const SupportTicketForm = ({ setTicketNumber }) => {
  const sortApis = (apis) => {
    return apis.sort((a, b) => a.name.localeCompare(b.name))
  };
  const [submitLoadingSpinner, setSubmitLoadingSpinner] = useState(false);
  const [apiSearch, setApiSearch] = useState("");
  const [apisFiltered, setApisFiltered] = useState(sortApis(apis));
  const debounceTimer = useRef(null);
  const [state, setState] = useState({
    role: "",
    name: "",
    email: "",
    sams_id: "",
    program: "",
    organization: "",
    organization_url: "",
    intermediary: "",
    issue_name: "",
    priority: "",
    service: "",
    api: [],
    description: "",
    environment: "",
    additional_info: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setState((prevFormData) => ({ ...prevFormData, [name]: value }));
  };



  const handleApiSearchChange = (event) => {
    const searchValue = event.target.value || "";
    setApiSearch(searchValue);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      if (searchValue) {
        const apisTemp = apis.filter(api =>
          api.name.toLowerCase().includes(searchValue.toLowerCase())
        );
        setApisFiltered(sortApis(apisTemp));
      } else {
        setApisFiltered(sortApis(apis));
      }
    }, 300);
  };

  const handleApiCheckboxChange = (event, apiName) => {
    setState(prevState => {
      const alreadyChecked = prevState.api.includes(apiName);
      const newApiArray = alreadyChecked
        ? prevState.api.filter(name => name !== apiName)
        : [...prevState.api, apiName];
      return { ...prevState, api: newApiArray };
    });
  };

  const isFormValid = () => {
    const { name, email } = state;

    return (
      name &&
      email
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitLoadingSpinner(true);

    const formattedDescription = `Role: ${state.role ?? 'Empty'}; ${state.description}`;
    const formattedApi = Array.isArray(state.api) ? state.api.join('; ') : state.api;

    const submissionState = {
      ...state,
      description: formattedDescription,
      api: formattedApi,
    };

    try {
      const data = await contactUsRequest(submissionState);
      setTicketNumber(data.request_number);
    } catch (error) {
      console.error("Error during form submission:", error);
    } finally {
      setSubmitLoadingSpinner(false); 
    }
  };

  return (
    <>
      {submitLoadingSpinner && <Spinner />}
      <div className="tablet:padding-x-5 padding-y-3 margin-x-5 ">
        <h1 className="patua-one-regular fs-40 mb-0">Submit a Support Ticket</h1>

        <div className="grid-col-12 tablet:grid-col-6 service-now-question-block desktop:grid-col-6 support">
          <p className="service-now-ticket-description support">
            Use the form below to help us get you the support you need. We
            capture all the essential information, but in some cases the Support
            Team may need to contact you for additional details.
          </p>
        </div>
      </div>
      <form className="bg-light-blue" onSubmit={handleSubmit}>
        <div className="tablet:padding-x-6 padding-y-4 margin-x-6 ">
        <h2 className="text-bold text-base-darkest">Contact Information</h2>
        <p>
          Required fields all are marked with an asterisk(
          <span className="service-now-asterisk">*</span>)
        </p>
        <div className="grid-row grid-gap">
          <div className="grid-col-12 tablet:grid-col-4 service-now-question-block support margin-bottom-6 desktop:grid-col-4">
            <ServiceNowQuestion
              question="Name"
              details="Provide your full name."
              optionalClass="support"
              requiredQuestion={true}
              label="name"
            />
            <input
              type="text"
              required
              id="name"
              aria-label="Enter your Name"
              name="name"
              value={state.name}
              onChange={handleChange}
              className="usa-input"
            />
          </div>
          <div className="grid-col-12 tablet:grid-col-4 service-now-question-block margin-bottom-6 desktop:grid-col-4 support">
            <ServiceNowQuestion
              question="Email"
              details=""
              requiredQuestion={true}
              optionalClass="support"
              label="email"
            />
            <input
              type="text"
              required
              id="email"
              aria-label="Enter your email"
              name="email"
              value={state.email}
              onChange={handleChange}
              className="usa-input"
            />
          </div>
          <div className="grid-col-12 tablet:grid-col-4 service-now-question-block margin-bottom-6 desktop:grid-col-4 support">
            <ServiceNowQuestion
              question="Company"
              details=""
              optionalClass="support"
              requiredQuestion={false}
              label="organization"
            />
            <input
              type="text"
              id="organization"
              aria-label="Enter your company"
              name="organization"
              value={state.organization}
              onChange={handleChange}
              className="usa-input"
            />
          </div>
          <div className="grid-col-12 tablet:grid-col-4 service-now-question-block margin-bottom-6 desktop:grid-col-4">
            <ServiceNowQuestion
              question="Role"
              details=""
              requiredQuestion={false}
              optionalClass="support"
              onChange={handleChange}
              label="role"
            />
            <form className="usa-form w-100">
              <select className="usa-select" name="role" id="role" onChange={handleChange}>
                <option value>- Select -</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Developer">Developer</option>
                <option value="Government">Government</option>
                <option value="Educator">Educator</option>
                <option value="Media">Media</option>
                <option value="Non-Profit">Non-Profit</option>
              </select>
            </form>
          </div>
        </div>
        <h2 className="text-bold text-base-darkest">Service Details</h2>
        <div className="grid-row grid-gap">
          <div>
            <ServiceNowQuestion
              question={"What APIs are you looking to access? Select all that apply. Click <a href=\"/apis\" target=\"_blank\" rel=\"noreferrer\">here</a> to read the descriptions of each of the APIs available to you."}
              optionalClass="support"
              details={""}
              requiredQuestion={false}
              label="apis"
            />
            <label className="usa-label" htmlFor="api-search">Search for an API</label>
            <input className="usa-input" id="api-search" name="api-search" placeholder="Type to search" aria-describedby="api-search-desc" value={apiSearch} onChange={handleApiSearchChange} />
            <FormGroup
              sx={{
                marginTop: 2,
                height: 200,
                width: '100%',
                overflowY: 'scroll',
                overflowX: 'auto',
                padding: 2,
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'nowrap',
                alignItems: 'flex-start',
              }}
            >
              {
                apisFiltered.length === 0 ? <p className="italic" aria-live="polite">No APIs found.</p> : apisFiltered.map((api) => (
                  <FormControlLabel 
                    key={api.id} control={<Checkbox
                          checked={state.api.includes(api.name)}
                          onChange={e => handleApiCheckboxChange(e, api.name)}
                      />} 
                    label={api.name} 
                    componentsProps={{
                      typography: {
                        sx: {
                          backgroundColor: 'white',
                          padding: '5px',
                          borderRadius: '4px',
                          border: '1px solid #2196F3',
                          marginBottom: '5px',
                        }
                      }
                    }}
                  />
              ))
              }
            </FormGroup>
          </div>
          <div className="grid-col-12 tablet:grid-col-12 service-now-question-block margin-bottom-12 desktop:grid-col-12">
            <ServiceNowQuestion
              question="What do you need the API(s) selected for?"
              optionalClass="support"
              details=""
              requiredQuestion={false}
              label="description"
            />
            <textarea
              className="usa-textarea single-item"
              type="text"
              name="description"
              id="description"
              aria-label="description details"
              rows="6"
              value={state.description}
              onChange={handleChange}
            />
          </div>
          </div>
          <div className="form-control flex end mt-10">
            <button
              className="usa-button usa-button--accent service-now-button"
              type="submit"
              disabled={!isFormValid()}
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default SupportTicketForm;
